import React, { useState, useEffect } from 'react';

const FileDiffViewer = ({ fileId, onClose }) => {
  const [diffData, setDiffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDiff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const fetchDiff = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/diff`
      );
      if (!response.ok) throw new Error('Failed to fetch diff');
      const data = await response.json();
      setDiffData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderLineDiff = () => {
    if (!diffData) return null;

    const originalLines = diffData.original_content.split('\n');
    const currentLines = diffData.current_content.split('\n');
    const maxLines = Math.max(originalLines.length, currentLines.length);

    return (
      <div className="grid grid-cols-2 gap-4 font-mono text-sm">
        {/* Original */}
        <div>
          <div className="bg-red-100 dark:bg-red-900/30 px-3 py-2 font-semibold text-red-800 dark:text-red-200 sticky top-0">
            Original
          </div>
          <div className="bg-red-50 dark:bg-red-900/10">
            {originalLines.map((line, idx) => (
              <div key={`orig-${idx}`} className="flex border-b border-red-200 dark:border-red-800">
                <span className="px-2 py-1 text-red-600 dark:text-red-400 w-12 flex-shrink-0 text-right bg-red-100 dark:bg-red-900/20">
                  {idx + 1}
                </span>
                <pre className="px-3 py-1 flex-1 overflow-x-auto text-gray-800 dark:text-gray-200">
                  {line || ' '}
                </pre>
              </div>
            ))}
          </div>
        </div>

        {/* Current */}
        <div>
          <div className="bg-green-100 dark:bg-green-900/30 px-3 py-2 font-semibold text-green-800 dark:text-green-200 sticky top-0">
            Current
          </div>
          <div className="bg-green-50 dark:bg-green-900/10">
            {currentLines.map((line, idx) => (
              <div key={`curr-${idx}`} className="flex border-b border-green-200 dark:border-green-800">
                <span className="px-2 py-1 text-green-600 dark:text-green-400 w-12 flex-shrink-0 text-right bg-green-100 dark:bg-green-900/20">
                  {idx + 1}
                </span>
                <pre className="px-3 py-1 flex-1 overflow-x-auto text-gray-800 dark:text-gray-200">
                  {line || ' '}
                </pre>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              File Diff
            </h3>
            {diffData && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {diffData.path}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="spinner"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {!loading && !error && diffData && (
            <>
              {!diffData.modified ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No changes detected
                </div>
              ) : (
                renderLineDiff()
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileDiffViewer;
